import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Breadcrumbs Component - Visual navigation breadcrumbs with Schema.org markup
 * @param {Array} items - Array of {label, href} objects (optional - auto-generates from URL)
 * @param {string} currentLabel - Label for current page (optional)
 */
export default function Breadcrumbs({ items, currentLabel }) {
  const location = useLocation();
  
  // Auto-generate breadcrumbs from URL if not provided
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) {
      return [{ label: 'Home', href: '/' }];
    }
    
    const crumbs = [{ label: 'Home', href: '/' }];
    
    // Handle service-location pages (e.g., fiber-optic-installation-miami)
    if (pathSegments.length === 1 && pathSegments[0].includes('-')) {
      const slug = pathSegments[0];
      
      // Check if it's a service-city combo
      const cityMatch = slug.match(/(.*?)-(miami|orlando|tampa|fort-lauderdale|jacksonville|west-palm-beach|tallahassee|panama-city|fort-myers|winter-haven|naples|sarasota|gainesville|ocala|daytona-beach|boca-raton|coral-springs|pompano-beach|hollywood|hialeah)$/);
      
      if (cityMatch) {
        const serviceSlug = cityMatch[1];
        const citySlug = cityMatch[2];
        
        // Format service name
        const serviceName = serviceSlug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Format city name
        const cityName = citySlug
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        crumbs.push({ label: 'Services', href: '/services' });
        crumbs.push({ label: `${serviceName} - ${cityName}`, href: null });
        
        return crumbs;
      }
    }
    
    // Standard breadcrumb generation
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      // Format segment name
      let label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Use custom label for last item if provided
      if (isLast && currentLabel) {
        label = currentLabel;
      }
      
      crumbs.push({
        label,
        href: isLast ? null : currentPath
      });
    });
    
    return crumbs;
  };
  
  const breadcrumbs = items || generateBreadcrumbs();
  
  // Don't render on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav 
      aria-label="Breadcrumb" 
      className="bg-gray-50 border-b border-gray-200"
      data-testid="breadcrumbs"
    >
      <div className="container mx-auto px-4 py-3">
        <ol 
          className="flex flex-wrap items-center gap-1 text-sm"
          itemScope 
          itemType="https://schema.org/BreadcrumbList"
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            
            return (
              <li 
                key={index}
                className="flex items-center"
                itemProp="itemListElement" 
                itemScope 
                itemType="https://schema.org/ListItem"
              >
                {index > 0 && (
                  <ChevronRight className="w-4 h-4 text-gray-400 mx-1" />
                )}
                
                {crumb.href && !isLast ? (
                  <Link 
                    to={crumb.href}
                    className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-1"
                    itemProp="item"
                  >
                    {index === 0 && <Home className="w-4 h-4" />}
                    <span itemProp="name">{crumb.label}</span>
                  </Link>
                ) : (
                  <span 
                    className="text-gray-800 font-medium flex items-center gap-1"
                    itemProp="name"
                  >
                    {index === 0 && <Home className="w-4 h-4" />}
                    {crumb.label}
                  </span>
                )}
                
                <meta itemProp="position" content={index + 1} />
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
